/**
 * OpenFreeMap → PMTiles tile-fallback flip controller (D-82 + UI-SPEC W1 +
 * Phase 11 D-16 5-second show-grace).
 *
 * Triggers fallback when:
 *   - MapLibre `error` event fires with `e.error.status >= 500` (HTTP 5xx),
 *     OR
 *   - ≥ 2 tile errors in a 60s sliding window (timeout/network proxy for
 *     "OFM is sustained-unavailable"),
 * AND THEN debounced for 5 seconds before the banner appears (D-16 — Phase
 * 11). Transient errors that resolve under 5s do NOT produce a visible
 * banner; the show-grace timer is cancelled by `retryPrimary()` or by the
 * disposal path. Per UI-SPEC line 384, even the HTTP 5xx fast-path now goes
 * through the 5s show-grace — banner appears at t=5s, not t=0s — because
 * transient 5xxs that resolve under 5s should not flicker.
 *
 * Hysteresis: after `retryPrimary()`, the controller enters a 60s grace
 * period during which any new error immediately re-flips to fallback (no
 * oscillation). After the grace expires, the threshold counter resets.
 * This 60s post-retry grace is PRESERVED verbatim from Phase 9 (TIMEOUT_
 * WINDOW_MS) and is independent of the new SHOW_GRACE_MS debounce.
 *
 * This module knows NOTHING about React. The only React touchpoint is the
 * `onFallbackFlip(active)` callback, which lifts state to the MapView so it
 * can render the TileFallbackBanner overlay.
 *
 * No DOM markers, no clusters. Layer visibility is toggled via
 * `setLayoutProperty('visibility', 'none' | 'visible')` on every layer
 * tagged as belonging to the OFM source vs the PMTiles source. Tag
 * resolution happens at attach time so we don't repeatedly walk the layer
 * list inside the error handler.
 */
import type { Map as MaplibreMap } from "maplibre-gl";

const TIMEOUT_WINDOW_MS = 60_000;
const TIMEOUT_THRESHOLD = 2;

/**
 * Phase 11 D-16 — 5-second debounce window applied before the banner is
 * actually shown. If a `retryPrimary()` (or successful tile load implied via
 * `flipTo(false)`) arrives within this window, the show-grace timer is
 * cancelled and the banner never appears. Documented in
 * 11-RESEARCH.md § 4.1 (lines 408-461) and 11-UI-SPEC.md § Surface 4.
 */
export const SHOW_GRACE_MS = 5_000;

 
type AttachFn = (map: MaplibreMap, ofmLayerIds: string[], pmtilesLayerIds: string[]) => void;
 
type FlipNotify = (active: boolean) => void;
 
type MaplibreErrorListener = (e: unknown) => void;

export interface TileFallbackController {
  attach: AttachFn;
  retryPrimary(): void;
  isFallbackActive(): boolean;
  dispose(): void;
}

export interface TileFallbackOptions {
  /** Notify React the flip happened so it can render/hide the banner. */
  onFallbackFlip: FlipNotify;
}

interface MaplibreErrorEvent {
  error?: { status?: number; message?: string };
}

export function createTileFallbackController(
  opts: TileFallbackOptions,
): TileFallbackController {
  let map: MaplibreMap | null = null;
  let ofmIds: string[] = [];
  let pmtIds: string[] = [];
  let timestamps: number[] = [];
  let fallbackActive = false;
  let primaryRetryGraceUntil = 0;
  // Phase 11 D-16 — pending show-grace timer. Non-null while a fallback flip
  // is scheduled but not yet committed; `retryPrimary()` (which calls
  // flipTo(false)) and `dispose()` both clear it via `clearTimeout`.
  let pendingFlipTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Apply the actual MapLibre layer-visibility toggles + fire the React
   * callback. Extracted from the original `flipTo` body so the show-grace
   * timer can defer it without duplicating the visibility loop.
   */
  const commitFlip = (toFallback: boolean) => {
    if (!map) return;
    for (const id of ofmIds) {
      try {
        map.setLayoutProperty(id, "visibility", toFallback ? "none" : "visible");
      } catch {
        // Layer may not exist yet during initial style swap; ignore.
      }
    }
    for (const id of pmtIds) {
      try {
        map.setLayoutProperty(id, "visibility", toFallback ? "visible" : "none");
      } catch {
        // ignore
      }
    }
    fallbackActive = toFallback;
    opts.onFallbackFlip(toFallback);
  };

  /**
   * Phase 11 D-16 — branched show-grace flipTo. Going-fallback (true) is
   * deferred via SHOW_GRACE_MS setTimeout; going-primary (false) is
   * synchronous and ALSO cancels any in-flight show-grace timer. See
   * 11-RESEARCH.md § 4.1 lines 419-461.
   */
  const flipTo = (toFallback: boolean) => {
    if (!map) return;
    if (toFallback) {
      // Going fallback-active. If we're already in fallback, no-op.
      if (fallbackActive) return;
      // If a show-grace timer is already pending, don't double-schedule.
      if (pendingFlipTimer) return;
      pendingFlipTimer = setTimeout(() => {
        pendingFlipTimer = null;
        // Apply layer toggles + fire callback HERE (5 s late). The map may
        // have been disposed during the grace window; commitFlip guards.
        commitFlip(true);
      }, SHOW_GRACE_MS);
    } else {
      // Going fallback-inactive (recovery / retryPrimary). If a show-grace
      // timer is pending, cancel it — we never fired the banner; nothing to
      // undo on the React side, and no layer-visibility toggle was applied.
      if (pendingFlipTimer) {
        clearTimeout(pendingFlipTimer);
        pendingFlipTimer = null;
        return;
      }
      // No pending timer; if we're not currently in fallback, no-op.
      if (!fallbackActive) return;
      commitFlip(false);
    }
  };

  const onError = (e: MaplibreErrorEvent) => {
    const status = e.error?.status;
    if (fallbackActive) return;
    // If we're inside the post-retry grace, any error → snap back to fallback.
    if (Date.now() < primaryRetryGraceUntil) {
      flipTo(true);
      return;
    }
    if (typeof status === "number" && status >= 500) {
      flipTo(true);
      return;
    }
    // Timeout-counter path: each error contributes one timestamp; if ≥ 2
    // accumulate inside the 60s window, flip.
    const now = Date.now();
    timestamps = timestamps.filter((t) => now - t < TIMEOUT_WINDOW_MS);
    timestamps.push(now);
    if (timestamps.length >= TIMEOUT_THRESHOLD) {
      flipTo(true);
    }
  };

  return {
    attach(m, ofmLayerIds, pmtilesLayerIds) {
      map = m;
      ofmIds = ofmLayerIds;
      pmtIds = pmtilesLayerIds;
      // MapLibre's error event payload uses any-shape compatible with our
      // narrow MaplibreErrorEvent interface.
      m.on("error", onError as MaplibreErrorListener);
    },
    retryPrimary() {
      if (!map) return;
      flipTo(false);
      primaryRetryGraceUntil = Date.now() + TIMEOUT_WINDOW_MS;
      timestamps = [];
    },
    isFallbackActive() {
      return fallbackActive;
    },
    dispose() {
      // Phase 11 D-16 — clear any in-flight show-grace timer FIRST so a
      // late-firing callback can't try to flip a disposed map.
      if (pendingFlipTimer) {
        clearTimeout(pendingFlipTimer);
        pendingFlipTimer = null;
      }
      if (map) {
        map.off("error", onError as MaplibreErrorListener);
        map = null;
      }
      timestamps = [];
    },
  };
}
